import isNil from 'lodash/isNil';
import SurveyItem from './SurveyItem';
import surveyUrls from './surveyUrls';
import ExtensionCollection from './ExtensionCollection';

export class IncompleteCalculation {
  readonly incomplete = true;
}

export class InvalidCalculation extends Error {}

type TokenType =
  | 'number'
  | 'ident'
  | 'string'
  | 'plus'
  | 'minus'
  | 'star'
  | 'slash'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'eof';

type Token = { type: TokenType; value?: string | number };

const IDENT = /^[A-Za-z_][A-Za-z0-9_.]*/;

const tokenize = (input: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }
    if (
      ch === '+' ||
      ch === '-' ||
      ch === '*' ||
      ch === '/' ||
      ch === '(' ||
      ch === ')' ||
      ch === ','
    ) {
      const type =
        ch === '+'
          ? 'plus'
          : ch === '-'
          ? 'minus'
          : ch === '*'
          ? 'star'
          : ch === '/'
          ? 'slash'
          : ch === '('
          ? 'lparen'
          : ch === ')'
          ? 'rparen'
          : 'comma';
      tokens.push({ type });
      i += 1;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const quote = ch;
      let j = i + 1;
      let value = '';
      while (j < input.length && input[j] !== quote) {
        value += input[j];
        j += 1;
      }
      if (j >= input.length) {
        throw new InvalidCalculation(
          `Unterminated string in expression: ${input}`,
        );
      }
      tokens.push({ type: 'string', value });
      i = j + 1;
      continue;
    }
    if ((ch >= '0' && ch <= '9') || ch === '.') {
      const match = input.slice(i).match(/^\d*\.?\d+(?:[eE][+-]?\d+)?/);
      if (!match) {
        throw new InvalidCalculation(`Invalid number in expression: ${input}`);
      }
      tokens.push({ type: 'number', value: Number(match[0]) });
      i += match[0].length;
      continue;
    }
    const identMatch = input.slice(i).match(IDENT);
    if (identMatch) {
      tokens.push({ type: 'ident', value: identMatch[0] });
      i += identMatch[0].length;
      continue;
    }
    throw new InvalidCalculation(`Unexpected character "${ch}" in expression`);
  }
  tokens.push({ type: 'eof' });
  return tokens;
};

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): Expr {
    const expr = this.expr();
    this.expect('eof');
    return expr;
  }

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    const token = this.tokens[this.pos];
    this.pos += 1;
    return token;
  }

  private expect(type: TokenType): Token {
    const token = this.consume();
    if (token.type !== type) {
      throw new InvalidCalculation(`Expected ${type}, got ${token.type}`);
    }
    return token;
  }

  private expr(): Expr {
    let node = this.term();
    while (this.peek().type === 'plus' || this.peek().type === 'minus') {
      const op = this.consume().type === 'plus' ? '+' : '-';
      node = { kind: 'binary', op, left: node, right: this.term() };
    }
    return node;
  }

  private term(): Expr {
    let node = this.unary();
    while (this.peek().type === 'star' || this.peek().type === 'slash') {
      const op = this.consume().type === 'star' ? '*' : '/';
      node = { kind: 'binary', op, left: node, right: this.unary() };
    }
    return node;
  }

  private unary(): Expr {
    if (this.peek().type === 'plus') {
      this.consume();
      return this.unary();
    }
    if (this.peek().type === 'minus') {
      this.consume();
      return { kind: 'unary', op: '-', arg: this.unary() };
    }
    return this.primary();
  }

  private primary(): Expr {
    const token = this.peek();
    if (token.type === 'number') {
      this.consume();
      return { kind: 'number', value: token.value as number };
    }
    if (token.type === 'string') {
      this.consume();
      return { kind: 'ref', linkId: token.value as string };
    }
    if (token.type === 'ident') {
      this.consume();
      if (this.peek().type === 'lparen') {
        this.consume();
        const args: Expr[] = [];
        if (this.peek().type !== 'rparen') {
          args.push(this.expr());
          while (this.peek().type === 'comma') {
            this.consume();
            args.push(this.expr());
          }
        }
        this.expect('rparen');
        return { kind: 'call', name: token.value as string, args };
      }
      return { kind: 'ref', linkId: token.value as string };
    }
    if (token.type === 'lparen') {
      this.consume();
      const inner = this.expr();
      this.expect('rparen');
      return inner;
    }
    throw new InvalidCalculation(`Unexpected token ${token.type}`);
  }
}

type Expr =
  | { kind: 'number'; value: number }
  | { kind: 'ref'; linkId: string }
  | { kind: 'unary'; op: '-'; arg: Expr }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/'; left: Expr; right: Expr }
  | { kind: 'call'; name: string; args: Expr[] };

const parseExpression = (expression: string): Expr =>
  new Parser(tokenize(expression)).parse();

const collectRefs = (expr: Expr, into: Set<string>): void => {
  if (expr.kind === 'ref') {
    into.add(expr.linkId);
    return;
  }
  if (expr.kind === 'unary') {
    collectRefs(expr.arg, into);
    return;
  }
  if (expr.kind === 'binary') {
    collectRefs(expr.left, into);
    collectRefs(expr.right, into);
    return;
  }
  if (expr.kind === 'call') {
    expr.args.forEach(arg => collectRefs(arg, into));
  }
};

export const referencedLinkIds = (expression: string): string[] => {
  try {
    const refs = new Set<string>();
    collectRefs(parseExpression(expression), refs);
    return [...refs];
  } catch {
    return [];
  }
};

const evalExpr = (
  expr: Expr,
  resolve: (linkId: string) => number | IncompleteCalculation,
): number | IncompleteCalculation => {
  if (expr.kind === 'number') {
    return expr.value;
  }
  if (expr.kind === 'ref') {
    return resolve(expr.linkId);
  }
  if (expr.kind === 'unary') {
    const arg = evalExpr(expr.arg, resolve);
    if (arg instanceof IncompleteCalculation) return arg;
    return -arg;
  }
  if (expr.kind === 'binary') {
    const left = evalExpr(expr.left, resolve);
    if (left instanceof IncompleteCalculation) return left;
    const right = evalExpr(expr.right, resolve);
    if (right instanceof IncompleteCalculation) return right;
    if (expr.op === '+') return left + right;
    if (expr.op === '-') return left - right;
    if (expr.op === '*') return left * right;
    if (right === 0) return new IncompleteCalculation();
    return left / right;
  }
  if (expr.name === 'weight') {
    if (expr.args.length !== 1 || expr.args[0].kind !== 'ref') {
      throw new InvalidCalculation('weight() expects one item reference');
    }
    return resolve(expr.args[0].linkId);
  }
  if (expr.name === 'max' || expr.name === 'min') {
    if (expr.args.length === 0) {
      throw new InvalidCalculation(
        `${expr.name}() expects at least one argument`,
      );
    }
    const values: number[] = [];
    for (const arg of expr.args) {
      const value = evalExpr(arg, resolve);
      if (value instanceof IncompleteCalculation) return value;
      values.push(value);
    }
    return expr.name === 'max' ? Math.max(...values) : Math.min(...values);
  }
  throw new InvalidCalculation(`Unknown function ${expr.name}`);
};

/**
 * Evaluate a calculated-value expression. Returns null when any referenced
 * source is unanswered (do not treat blank as 0) or the expression is invalid.
 */
export const evaluateExpression = (
  expression: string,
  resolve: (linkId: string) => number | null,
): number | null => {
  try {
    const result = evalExpr(parseExpression(expression), linkId => {
      const value = resolve(linkId);
      return value == null ? new IncompleteCalculation() : value;
    });
    if (result instanceof IncompleteCalculation) return null;
    if (!Number.isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
};

export const isAnsweredValue = (rawValue: unknown): boolean => {
  if (isNil(rawValue)) return false;
  if (rawValue === '') return false;
  if (Array.isArray(rawValue)) return rawValue.length > 0;
  return true;
};

const codingNumeric = (coding: fhir.Coding): number | null => {
  const fromExt = ExtensionCollection.initFromBackboneElement(
    coding,
  ).getValueDecimal(surveyUrls.surveyListOptionNumericValue);
  return isNil(fromExt) ? null : fromExt;
};

export type ChoiceWeightLookup = (code: string) => number | null;

const choiceWeightFromLookup = (
  lookup: ChoiceWeightLookup | undefined,
  code: string | undefined,
): number | null => {
  if (!lookup || isNil(code) || code === '') return null;
  const weight = lookup(code);
  return isNil(weight) ? null : weight;
};

/**
 * Numeric value of an answered item. Choice answers use
 * survey-list-option-numeric-value on the selected option (inline
 * answerOption) or a value-set / choice-options lookup when optionList
 * is empty (builder lists). Returns null when unanswered or when a
 * choice has no configured weight.
 */
export const numericFromAnswer = (
  item: SurveyItem,
  rawValue: unknown,
  lookupChoiceWeight?: ChoiceWeightLookup,
): number | null => {
  if (!isAnsweredValue(rawValue)) return null;
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }
  if (
    rawValue &&
    typeof rawValue === 'object' &&
    !Array.isArray(rawValue) &&
    'value' in rawValue &&
    typeof (rawValue as fhir.Quantity).value === 'number'
  ) {
    return (rawValue as fhir.Quantity).value ?? null;
  }
  if (typeof rawValue === 'string') {
    const option = item.optionList.find(opt => opt.value === rawValue);
    if (option && !isNil(option.numericValue)) {
      return option.numericValue;
    }
    const fromLookup = choiceWeightFromLookup(lookupChoiceWeight, rawValue);
    if (!isNil(fromLookup)) return fromLookup;
    if (item.type === 'choice' || item.type === 'open-choice') {
      return null;
    }
    const parsed = Number(rawValue);
    return rawValue.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
  }
  if (rawValue && typeof rawValue === 'object' && 'code' in rawValue) {
    const coding = rawValue as fhir.Coding;
    const fromAnswer = codingNumeric(coding);
    if (!isNil(fromAnswer)) return fromAnswer;
    const option = item.optionList.find(opt => opt.formValue === coding.code);
    if (option && !isNil(option.numericValue)) {
      return option.numericValue;
    }
    return choiceWeightFromLookup(lookupChoiceWeight, coding.code);
  }
  return null;
};

export const roundToPrecision = (
  value: number,
  precision: number | null,
): number => {
  const places = isNil(precision) ? 10 : precision;
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
};
